import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

import SuartePlusIcon from '@/assets/all-icons/suarte-plus-icon';
import config from '@/config';
import ProfileDataContext from '@/context/ProfileDataContext';
import useCache from '@/hooks/useCache';
import useStateHandler from '@/hooks/useStateHandler';
import useTranslations from '@/i18n/useTranslations';
import ContinueButton from '@/shared-components/buttons/components/ContinueButton';
import ProfileAddButton from '@/shared-components/buttons/components/ProfileAddButton';
import ProfileContentLoader from '@/shared-components/loader/special-loaders/ProfileContentLoader.jsx';
import ActionRejectedPopup from '@/shared-components/popups/components/ActionRejectedPopup';
import SuartePlusPopup from '@/shared-components/popups/components/SuartePlusPopup';
import Text from '@/shared-components/text/components/Text';
import { canAccessGalleryPermission } from '@/utils/galleryPermissions';

import DraftList from '../drafts/components/DraftList';
import PopUpDrafts from '../drafts/components/PopUpDrafts';
import MetaDescripProfilePages from '../MetaDescripProfilePages';
import PopUpCreateOptionsBoard from '../post/components/PopUpCreateOptionsBoard';

import ArticlesVirtualList from './components/ArticlesVirtualList';
import PostVirtualList    from './components/PostVirtualList';
import useScheduledBoardRefresh from './hooks/useScheduledBoardRefresh';
import { partitionBoardItems } from './utils/partitionBoardItems';

import './index.css';

const EMPTY_PAGINATED_RESULT = {
  data: [],
  totalDocs: 0,
};

function hasArticleTitle(item) {
  return Boolean(item?.post_container?.post_title?.trim());
}

export default function ProfileBoard() {
  const t = useTranslations('common.profile.pages.board');
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { profileData, internal } = useContext(ProfileDataContext);
  const { state } = useStateHandler();
  const profileId = profileData?._id;

  const [openOptions, setOpenOptions] = useState(false);
  const [openDraftPopup, setOpenDraftPopup] = useState(false);
  const [showSuartePlusPopup, setShowSuartePlusPopup] = useState(false);
  const [showActionRejectedPopup, setShowActionRejectedPopup] = useState(false);
  const [hasOpenedPostsTab, setHasOpenedPostsTab] = useState(
    searchParams.get('view') !== 'articles'
  );
  const [hasOpenedArticlesTab, setHasOpenedArticlesTab] = useState(
    searchParams.get('view') === 'articles'
  );

  const userType = String(profileData?.user_type ?? profileData?.__t ?? "").toLowerCase();
  const isGallery = userType === "gallery";
  const isArtist = userType === "artist";
  const hasSuartePlus = Boolean(profileData?.suartePlus?.isActive);
  const session = state.user_session;
  const sessionType = String(session?.user_type ?? session?.__t ?? "").toLowerCase();
  const sessionId = session?._id ?? session?.user_id ?? null;
  const isGalleryOwner = sessionType === "gallery" && sessionId === profileId;
  const isArtistOwner = sessionType === "artist" && sessionId === profileId;
  const allowPosts = isGallery
    ? (isGalleryOwner || canAccessGalleryPermission(session, "board.post", profileId))
    : isArtist
      ? isArtistOwner
      : hasSuartePlus;
  const allowArticles = isGallery
    ? (isGalleryOwner || canAccessGalleryPermission(session, "board.article", profileId))
    : isArtist
      ? isArtistOwner
      : false;
  const canCreatePosts = internal && (isGallery || isArtist || hasSuartePlus);
  const canOpenCreateOptions = allowPosts || allowArticles;
  const showSuartePlusUpsell = internal && !isGallery && !isArtist && !hasSuartePlus;
  const availableTabs = useMemo(
    () => ((isGallery || isArtist) ? ['posts', 'articles'] : ['posts']),
    [isArtist, isGallery]
  );
  const defaultTab = availableTabs[0];
  const rawView = searchParams.get('view');
  const view = availableTabs.includes(rawView) ? rawView : defaultTab;

  useEffect(() => {
    if (view === 'posts' && !hasOpenedPostsTab) {
      setHasOpenedPostsTab(true);
    }

    if (view === 'articles' && !hasOpenedArticlesTab) {
      setHasOpenedArticlesTab(true);
    }
  }, [hasOpenedArticlesTab, hasOpenedPostsTab, view]);

  const {
    loading: postsLoading,
    fetchData: postsFetchData,
    loadMoreData: loadMorePosts,
    refetch: refetchPosts,
  } = useCache(
    `${profileId}_posts`,
    `${config.apis.api.url}/posts/${userType}/posts/${profileId}`,
    {
      injectToken: true,
      disabled: !hasOpenedPostsTab,
      invalidateWhen: internal
        ? ['SUBMIT_POST', 'DELETE_POST', `${profileId}_REFRESH`]
        : [`${profileId}_REFRESH`],
    }
  );
  const safePosts = postsFetchData ?? EMPTY_PAGINATED_RESULT;
  const partitionedPosts = useMemo(
    () => partitionBoardItems(safePosts.data),
    [safePosts.data]
  );
  const publishedPosts = partitionedPosts.published;
  const queuedPosts = partitionedPosts.queued;
  const postsHasItems = publishedPosts.length > 0 || queuedPosts.length > 0;

  const {
    loading: articlesLoading,
    fetchData: articlesFetchData,
    loadMoreData: loadMoreArticles,
    refetch: refetchArticles,
  } = useCache(
    `${profileId}_articles`,
    `${config.apis.api.url}/posts/${userType}/articles/${profileId}`,
    {
      injectToken: true,
      disabled: !hasOpenedArticlesTab,
      invalidateWhen: internal
        ? ['SUBMIT_POST', 'DELETE_POST', `${profileId}_REFRESH`]
        : [`${profileId}_REFRESH`],
    }
  );
  const safeArticles = articlesFetchData ?? EMPTY_PAGINATED_RESULT;
  const partitionedArticles = useMemo(
    () => partitionBoardItems(safeArticles.data, hasArticleTitle),
    [safeArticles.data]
  );
  const publishedArticlesCount = partitionedArticles.published.length;
  const articlesHasContent =
    partitionedArticles.published.length > 0 ||
    partitionedArticles.queued.length > 0;
  const boardSeoCount = view === 'articles'
    ? publishedArticlesCount
    : publishedPosts.length;

  useScheduledBoardRefresh(queuedPosts, refetchPosts);
  useScheduledBoardRefresh(partitionedArticles.queued, refetchArticles);

  // ensure the query param always matches one of the available tabs
  useEffect(() => {
    if (!rawView || !availableTabs.includes(rawView)) {
      setSearchParams({ view: defaultTab }, { replace: true });
    }
  }, [availableTabs, defaultTab, rawView, setSearchParams]);

  useEffect(() => {
    if (!isGallery && !isArtist) return;
    if (!hasOpenedArticlesTab) return;

    if (
      view === 'posts' &&
      postsLoading === false &&
      postsHasItems === false &&
      articlesHasContent
    ) {
      setSearchParams(
        { view: 'articles' },
        { replace: true, preventScrollReset: true, state: location.state }
      );
    }
  }, [
    articlesHasContent,
    isArtist,
    isGallery,
    hasOpenedArticlesTab,
    location.state,
    postsHasItems,
    postsLoading,
    setSearchParams,
    view,
  ]);

  return (
    <>
      <MetaDescripProfilePages
        fetchData={profileData}
        subPage="board"
        internal={internal}
        itemCount={boardSeoCount}
        boardView={view}
      />
      {canCreatePosts && (
        <>
          <ProfileAddButton
            label={t('buttons.post_to_board')}
            onClick={() => {
              if (canOpenCreateOptions) {
                setOpenOptions(true);
                return;
              }
              setShowActionRejectedPopup(true);
            }}
          />

          <PopUpCreateOptionsBoard
            open={openOptions}
            close={() => setOpenOptions(false)}
            openDraftPopup={setOpenDraftPopup}
            queuedPosts={queuedPosts}
            queuedArticles={partitionedArticles.queued}
            allowPosts={allowPosts}
            allowArticles={allowArticles}
          />

          <PopUpDrafts
            open={openDraftPopup}
            close={() => setOpenDraftPopup(false)}
            DraftsList={props => <DraftList {...props} />}
          />
          <ActionRejectedPopup
            open={showActionRejectedPopup}
            close={() => setShowActionRejectedPopup(false)}
            description={t('messages.permission_denied')}
          />
        </>
      )}
      {/* ————— Tabs ————— */}
      <div className="user-profile__board-navigation element-non-selectable">
        {availableTabs.map(tab => (
          <div
            key={tab}
            className={`user-profile__board-navigation-link ${
              view === tab ? 'active' : ''
            }`}
            onClick={() => {
              if (tab === 'articles') {
                setHasOpenedArticlesTab(true);
              }
              if (tab === 'posts') {
                setHasOpenedPostsTab(true);
              }
              setSearchParams({ view: tab });
            }}
          >
            <Text
              className="user-profile__board-navigation-link-text"
            >
              {tab === 'posts' ? t('tabs.posts') : t('tabs.articles')}
            </Text>
          </div>
        ))}
      </div>
      {showSuartePlusUpsell && (
        <div className="user-profile__board-upsell-wrapper">
          <Text className="user-profile__board-upsell font-size-small" paragraph>
            {t('upsell.prefix')}{" "}
            <SuartePlusIcon className="user-profile__board-upsell-icon" aria-hidden="true" /> {t('upsell.middle')} {t('upsell.suffix')}
          </Text>
          <ContinueButton
            onClick={() => setShowSuartePlusPopup(true)}
          >
            {t('upsell.cta')}
          </ContinueButton>
        </div>
      )}
      {/* ————— Content Area ————— */}
      {showSuartePlusUpsell ? (
        <></>
      ) : view === 'posts' ? (
        <PostsTab
          key={`posts-${location.key}`}
          internal={internal}
          loading={postsLoading}
          items={safePosts}
          publishedItems={publishedPosts}
          queuedItems={queuedPosts}
          onLoadMore={loadMorePosts}
          profileName={profileData?.user_username || profileData?.user_name || t('defaults.profile_name')}
        />
      ) : (
        <>
          {(isGallery || isArtist) && (
            <ArticlesTab
              key={`articles-${location.key}`}
              internal={internal}
              articlesState={{
                loading: articlesLoading,
                items: safeArticles,
                publishedItems: partitionedArticles.published,
                queuedItems: partitionedArticles.queued,
                onLoadMore: loadMoreArticles,
              }}
            />
          )}
        </>
      )}
      <SuartePlusPopup
        open={showSuartePlusPopup}
        onClose={() => setShowSuartePlusPopup(false)}
        highlightedBenefit="posts"
      />
    </>
  );
}

function PostsTab(
  {
    internal,
    loading,
    items,
    publishedItems,
    queuedItems,
    onLoadMore,
    profileName,
  },
) {
  const t = useTranslations('common.profile.pages.board');
  const hasQueued = queuedItems.length > 0;
  const displayItems = publishedItems;
  const shouldLoadMore = items.data.length < items.totalDocs;
  const autoLoadCheckpointRef = useRef(0);

  useEffect(() => {
    if (displayItems.length === 0 && shouldLoadMore && items.data.length > autoLoadCheckpointRef.current) {
      autoLoadCheckpointRef.current = items.data.length;
      onLoadMore();
    }
    if (!shouldLoadMore) {
      autoLoadCheckpointRef.current = 0;
    }
  }, [displayItems.length, shouldLoadMore, items.data.length, onLoadMore]);

  if (loading) {
    return <ProfileContentLoader className="profile-board__posts-spinner" />;
  }

  if (publishedItems.length === 0 && queuedItems.length === 0) {
    return (
      <Text className="user-profile__board-empty-view font-size-medium" paragraph>
        {internal
          ? t('empty.posts_internal')
          : t('empty.posts_external', { name: profileName })}
      </Text>
    );
  }

  if (publishedItems.length === 0 && hasQueued) {
    return (
      <Text className="posts-queued__empty-hint font-size-small" paragraph>
        {internal
          ? t('empty.posts_queued_internal')
          : t('empty.posts_queued_external', { name: profileName })}
      </Text>
    );
  }

  return (
    <PostVirtualList
      items={{ ...items, data: displayItems }}
      onLoadMore={onLoadMore}
    />
  );
}

/** ─── ARTICLES TAB ───────────────────────────────────────────────────── */
function ArticlesTab(
  { internal, articlesState },
) {
  const t = useTranslations('common.profile.pages.board');
  const {
    loading = false,
    items = EMPTY_PAGINATED_RESULT,
    publishedItems = [],
    queuedItems = [],
    onLoadMore = () => {},
  } = articlesState ?? {};

  if (loading) {
    return <ProfileContentLoader className="profile-board__posts-spinner" />;
  }

  if (publishedItems.length === 0 && queuedItems.length === 0) {
    return (
      <Text className="user-profile__board-empty-view font-size-medium" paragraph>
        {internal
          ? t('empty.articles_internal')
          : t('empty.articles_external')}
      </Text>
    );
  }

  return (
    <ArticlesVirtualList
      items={items}
      publishedItems={publishedItems}
      queuedItems={queuedItems}
      onLoadMore={onLoadMore}
      cardVariant="board"
    />
  );
}
